
import React, { useState, useEffect } from 'react';
import { Layout } from './components/Layout';
import { UserDashboard } from './components/UserDashboard';
import { AdminPanel } from './components/AdminPanel';
import { KYCView } from './components/views/KYCView';
import { KYBView } from './components/views/KYBView';
import { AMLView } from './components/views/AMLView';
import { LEIView } from './components/views/LEIView';
import { ESGView } from './components/views/ESGView';
import { BlockchainView } from './components/views/BlockchainView';
import { IBANView } from './components/views/IBANView';
import { ReputationView } from './components/views/ReputationView';
import { PhoneView } from './components/views/PhoneView';
import { EmailView } from './components/views/EmailView';
import { MarketView } from './components/views/MarketView';
import { DarkwebView } from './components/views/DarkwebView';
import { AuthView } from './components/views/AuthView';
import { PricingView } from './components/views/PricingView';
import { PaymentView } from './components/views/PaymentView';
import { SettingsView } from './components/views/SettingsView';
import { LandingPage } from './components/LandingPage';
import { ContactView } from './components/views/ContactView';
import { AboutView } from './components/views/AboutView';
import { UseCasesView } from './components/views/UseCasesView';
import { ViewMode, NavigationPage, AuthState, SubscriptionTier } from './types';
import { Language } from './translations';

const App: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('user');
  const [currentPage, setCurrentPage] = useState<NavigationPage>('dashboard');
  const [authState, setAuthState] = useState<AuthState>('landing');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier | null>(null);
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'en' || saved === 'fr') ? saved : 'en';
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark' || 
           (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const renderAuthenticatedContent = () => {
    if (viewMode === 'admin') return <AdminPanel />;

    switch (currentPage) {
      case 'ekyc': return <KYCView onBack={() => setCurrentPage('dashboard')} />;
      case 'kyb': return <KYBView onBack={() => setCurrentPage('dashboard')} />;
      case 'aml': return <AMLView onBack={() => setCurrentPage('dashboard')} />;
      case 'lei': return <LEIView onBack={() => setCurrentPage('dashboard')} />;
      case 'blockchain': return <BlockchainView onBack={() => setCurrentPage('dashboard')} />;
      case 'iban': return <IBANView onBack={() => setCurrentPage('dashboard')} />;
      case 'esg': return <ESGView onBack={() => setCurrentPage('dashboard')} />;
      case 'reputation': return <ReputationView onBack={() => setCurrentPage('dashboard')} />;
      case 'phone': return <PhoneView onBack={() => setCurrentPage('dashboard')} />;
      case 'email': return <EmailView onBack={() => setCurrentPage('dashboard')} />;
      case 'market': return <MarketView onBack={() => setCurrentPage('dashboard')} />;
      case 'darkweb': return <DarkwebView onBack={() => setCurrentPage('dashboard')} />;
      case 'settings': return <SettingsView plan={selectedPlan || 'Free'} onBack={() => setCurrentPage('dashboard')} />;
      case 'contact': return <ContactView onBack={() => setCurrentPage('dashboard')} isPublic={false} language={language} />;
      case 'dashboard':
      default:
        return <UserDashboard onNavigate={setCurrentPage} language={language} />;
    }
  };

  // Auth Flow Guards
  if (authState === 'landing') {
    return <LandingPage setAuthState={setAuthState} darkMode={darkMode} setDarkMode={setDarkMode} language={language} setLanguage={setLanguage} />;
  }

  if (authState === 'public-contact') {
    return <ContactView onBack={() => setAuthState('landing')} isPublic={true} language={language} />;
  }

  if (authState === 'demo') {
    return <ContactView onBack={() => setAuthState('landing')} isPublic={true} isDemo={true} language={language} />;
  }

  if (authState === 'about') {
    return <AboutView onBack={() => setAuthState('landing')} language={language} />;
  }

  if (authState === 'use-cases') {
    return <UseCasesView onBack={() => setAuthState('landing')} language={language} />;
  }

  if (authState === 'login' || authState === 'register') {
    return <AuthView type={authState} setAuthState={setAuthState} />;
  }

  if (authState === 'pricing') {
    return <PricingView onSelectPlan={(plan) => {
      setSelectedPlan(plan);
      setAuthState('payment');
    }} />;
  }

  if (authState === 'payment') {
    return <PaymentView 
      plan={selectedPlan!} 
      onSuccess={() => setAuthState('authenticated')} 
      onBack={() => setAuthState('pricing')} 
    />;
  }

  return (
    <Layout 
      viewMode={viewMode} 
      setViewMode={setViewMode} 
      currentPage={currentPage} 
      onNavigate={setCurrentPage}
      plan={selectedPlan || 'Free'}
      onLogout={() => setAuthState('landing')}
      darkMode={darkMode}
      setDarkMode={setDarkMode}
      language={language}
      setLanguage={setLanguage}
    >
      {renderAuthenticatedContent()}
    </Layout>
  );
};

export default App;
